import React, { useState } from "react";
import { TabNav } from "@optimizely/axiom";

import { UserFormPrincipalUsers } from "./UserFormPrincipalUsers";

import styles from "./UserFormTabs.module.scss";

export const USER_FORM_TAB = {
    DETAILS: "details",
    PRINCIPAL_USERS: "principal-users"
};

/**
 * Splits the virtual teammate sidebar into Details and Principal users tabs. Only rendered for
 * VAUs — human users keep the flat, untabbed form, so their sidebar is unchanged.
 *
 * `children` is the existing form body and becomes the Details tab.
 *
 * Button ids are prefixed with `user-form-` because UserGroupFormTabs already owns a bare
 * `details-tab` id. The two never mount together today, but ids are global and the collision
 * would be silent.
 *
 * The click handler lives only on `TabNav.Tab`, which renders the `<li>`. A click on the inner
 * button bubbles up to it, and `Tab` declares `onClick` as required, so handling it in both
 * places would just fire the handler twice.
 */
export const UserFormTabs = ({ email, children }: { email: string; children: React.ReactNode }) => {
    const [currentTab, setCurrentTab] = useState<string>(USER_FORM_TAB.DETAILS);

    const handleTabClick = ({ event, tab }: { event: React.SyntheticEvent; tab: string }) => {
        event.preventDefault();
        setCurrentTab(tab);
    };

    return (
        <div aria-label="User Form Tabs" className={styles["user-form-tabs__container"]}>
            <TabNav activeTab={currentTab} className="push-double--bottom" style={["dashboard"]}>
                <TabNav.Tab
                    tabId={USER_FORM_TAB.DETAILS}
                    className={styles["user-form-tabs__tab"]}
                    onClick={(event: React.SyntheticEvent) => handleTabClick({ event, tab: USER_FORM_TAB.DETAILS })}
                >
                    <button
                        className={styles["user-form-tabs__button"]}
                        id="user-form-details-tab"
                        role="tab"
                        tabIndex={-1}
                        type="button"
                    >
                        Details
                    </button>
                </TabNav.Tab>
                <TabNav.Tab
                    tabId={USER_FORM_TAB.PRINCIPAL_USERS}
                    className={styles["user-form-tabs__tab"]}
                    onClick={(event: React.SyntheticEvent) =>
                        handleTabClick({ event, tab: USER_FORM_TAB.PRINCIPAL_USERS })
                    }
                >
                    <button
                        className={styles["user-form-tabs__button"]}
                        id="user-form-principal-users-tab"
                        role="tab"
                        tabIndex={-1}
                        type="button"
                    >
                        Principal users
                    </button>
                </TabNav.Tab>
            </TabNav>
            {currentTab === USER_FORM_TAB.DETAILS && children}
            {currentTab === USER_FORM_TAB.PRINCIPAL_USERS && <UserFormPrincipalUsers email={email} />}
        </div>
    );
};

UserFormTabs.displayName = "UserFormTabs";
